
import { Options, Vue } from 'vue-class-component';
import { ProviderDueDiligenceClient, SurveyTemplateClient } from '@/services/DueDiligenceServices';
import RiskLevelLegendModal from '@/components/modals/riskLevelLegendModal.vue';
import * as DDM from '@/dueDiligenceModels';
import store from '@/store';

@Options({})
export default class DueDiligenceDetail extends Vue {

    detail: DDM.ProviderProfileDetailVM = new DDM.ProviderProfileDetailVM();
    surveyTemplate: DDM.SurveyTemplate = null;
    loaded: boolean = false;
    activeTab: number = 1;
    expandedSections: number[] = [];
    selectedFactoryId: string = "";

    get culture(): string {
        return store.state.loggedUser?.preferredCulture || "en-US";
    }

    get submittedFactoryDetails(): DDM.FactorySurveyDetail[] {
        return (this.detail.factoryDetails || []).filter(fd => fd.hasResponse && fd.responseStatus === DDM.SurveyResponseStatus.Submitted);
    }

    get selectedSurveyResponse(): DDM.SurveyResponse | null {
        if (!this.detail.surveyResponses || this.detail.surveyResponses.length === 0) {
            return this.detail.surveyResponse || null;
        }
        if (this.selectedFactoryId) {
            return this.detail.surveyResponses.find(r => r.factoryId === this.selectedFactoryId) || null;
        }
        // Legacy: return first submitted
        return this.detail.surveyResponses.find(r => r.status === DDM.SurveyResponseStatus.Submitted) || this.detail.surveyResponse || null;
    }

    get registry(): DDM.ProviderRegistryInfo | null {
        return this.detail.profile.registry || null;
    }

    get registryGroups(): { title: string; fields: { label: string; value: string }[] }[] {
        const r = this.registry;
        if (!r) return [];
        const g = (title: string, pairs: [string, string][]) => ({
            title,
            fields: pairs
                .filter(p => p[1] && p[1].trim())
                .map(p => ({ label: p[0], value: p[1] }))
        });
        const groups = [
            g('Identificativi', [
                ['Tipo soggetto', r.subjectType], ['Codice', r.code], ['Tipo', r.entityType],
                ['Stato', r.status], ['Partita IVA', r.vatNumber], ['Codice Fiscale', r.fiscalCode],
                ['Soggetto IVA', r.vatSubject], ['Sogg. Rit. Acconto', r.withholdingTaxSubject],
                ['CF/PI errati', r.taxCodeError]
            ]),
            g('Sede legale', [
                ['Indirizzo', r.address], ['CAP', r.postalCode], ['Comune', r.municipality],
                ['Località', r.locality], ['Provincia', r.province], ['Nazione', r.country],
                ['Lingua', r.language]
            ]),
            g('Contatti', [
                ['Pref. tel. nazionale', r.nationalPhonePrefix], ['Pref. tel. locale', r.localPhonePrefix],
                ['Telefono', r.phone], ['Fax', r.fax], ['Telex', r.telex], ['Email', r.email],
                ['WEB', r.web], ['EDI', r.edi], ['Gruppo EDI', r.ediGroup]
            ]),
            g('Commerciale / Altro', [
                ['Ragione sociale', r.companyName], ['Sigla', r.acronym], ['Titolo', r.title],
                ['Tipologia', r.typology], ['Conto anticipi', r.advanceAccount], ['Fido', r.creditLimit],
                ['CONAI', r.conai], ['Cod. Cli/For Libero', r.freeCode]
            ])
        ];
        return groups.filter(grp => grp.fields.length > 0);
    }

    created() {
        this.init();
    }

    init() {
        let profileId = this.$route.params.profileId as string;
        ProviderDueDiligenceClient.getProviderProfileDetail(profileId)
        .then(x => {
            this.detail = x;
            this.loaded = true;

            // Auto-select first submitted factory
            let submitted = this.submittedFactoryDetails;
            if (submitted.length > 0) {
                this.selectedFactoryId = submitted[0].factoryId;
            }

            if (x.hasSurvey) {
                SurveyTemplateClient.getByCompany(false)
                .then(t => {
                    this.surveyTemplate = t;
                });
            }
        });
    }

    onFactoryChange() {
        // Response data is already loaded in surveyResponses, no extra call needed
    }

    getStatusLabel(status: DDM.ProviderProfileStatus): string {
        return DDM.getStatusLabel(status);
    }

    getStatusClass(status: DDM.ProviderProfileStatus): string {
        switch (status) {
            case DDM.ProviderProfileStatus.Invited: return "bg-secondary";
            case DDM.ProviderProfileStatus.Accepted: return "bg-info";
            case DDM.ProviderProfileStatus.DataCompleted: return "bg-primary";
            case DDM.ProviderProfileStatus.SurveyCompleted: return "bg-success";
            default: return "bg-secondary";
        }
    }

    getRiskLevelLabel(level: DDM.RiskLevel): string {
        return DDM.getRiskLevelLabel(level);
    }

    getRiskLevelClass(level: DDM.RiskLevel): string {
        return DDM.getRiskLevelClass(level);
    }

    getLocalized(ls: DDM.LocalizedString): string {
        if (!ls || !ls.values) return "";
        return ls.values[this.culture] || ls.values["en-US"] || Object.values(ls.values)[0] || "";
    }

    toggleSection(index: number) {
        let pos = this.expandedSections.indexOf(index);
        if (pos >= 0) {
            this.expandedSections.splice(pos, 1);
        } else {
            this.expandedSections.push(index);
        }
    }

    isQuestionVisible(question: DDM.SurveyQuestion): boolean {
        if (!question || !question.conditionalOnQuestionId) return true;
        if (!this.selectedSurveyResponse || !this.selectedSurveyResponse.answers) return true;
        return DDM.isConditionalMet(question.conditionalOnQuestionId, this.selectedSurveyResponse.answers);
    }

    getAnswerValue(questionId: string, subQuestionId: string): DDM.SurveyAnswer {
        if (!this.selectedSurveyResponse) return new DDM.SurveyAnswer();
        let found = this.selectedSurveyResponse.answers.find(
            a => a.questionId === questionId && a.subQuestionId === (subQuestionId || "")
        );
        return found || new DDM.SurveyAnswer();
    }

    getAnswerAttachment(questionId: string, subQuestionId: string): DDM.UrlAttachment | null {
        let answer = this.getAnswerValue(questionId, subQuestionId);
        return answer && answer.attachment ? answer.attachment : null;
    }

    getAnswerBadgeClass(response: string): string {
        switch (response) {
            case 'yes': return 'badge-yes';
            case 'partially': return 'badge-partially';
            case 'no': return 'badge-no';
            default: return 'badge-none';
        }
    }

    isCertSelected(questionId: string, certificationId: string): boolean {
        let answer = this.getAnswerValue(questionId, "");
        return answer.certificationSelections?.some(c => c.certificationId === certificationId && c.selected) || false;
    }

    hasAnyCertSelected(questionId: string, certOptions: DDM.CertificationOption[]): boolean {
        return certOptions.some(c => this.isCertSelected(questionId, c.certificationId));
    }

    getRedFlag(flagType: DDM.RedFlagType, response: string): { label: string; cssClass: string } | null {
        return DDM.getTriggeredRedFlag(flagType, response);
    }

    showRiskLevelLegend() {
        this.$opModal.show(RiskLevelLegendModal, {
            closeCb: () => { this.$opModal.closeLast(); }
        });
    }

    downloadPdf() {
        let profileId = this.$route.params.profileId as string;
        ProviderDueDiligenceClient.downloadSurveyPdf(profileId, this.culture, this.selectedFactoryId || undefined);
    }
}
